import type { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import type {
  AriaAttributes,
  ComponentProps,
  ReactElement,
  ReactNode,
} from 'react'

type TooltipCollisionBoundary = ComponentProps<
  typeof BaseTooltip.Positioner
>['collisionBoundary']

/**
 * Tooltip placement side
 */
export const TooltipSide = {
  Top: 'top',
  Bottom: 'bottom',
  Left: 'left',
  Right: 'right',
} as const

export type TooltipSide = (typeof TooltipSide)[keyof typeof TooltipSide]

/**
 * Tooltip alignment relative to the trigger
 */
export const TooltipAlign = {
  Start: 'start',
  Center: 'center',
  End: 'end',
} as const

export type TooltipAlign = (typeof TooltipAlign)[keyof typeof TooltipAlign]

/** Props for the simple Tooltip wrapper. */
export type TooltipProps = Pick<AriaAttributes, 'aria-expanded'> & {
  /** Tooltip content (text or ReactNode) */
  content: ReactNode
  /**
   * Optional node rendered next to `content` in a slot with consistent spacing.
   * Typically a `<Hotkey binding="..." />` or `<Kbd>` element. Children that emit
   * `data-slot="hotkey"` automatically get a `bg-surface-secondary` background so
   * they stay visible against the tooltip's primary surface.
   */
  hotkey?: ReactNode
  /** Placement side relative to trigger */
  side?: TooltipSide
  /** Alignment along the side axis */
  align?: TooltipAlign
  /** Distance from the trigger in pixels */
  sideOffset?: number
  /**
   * Element the popup must stay inside; it flips side when the trigger nears
   * that edge. Defaults to the popup's clipping ancestors (the viewport once
   * portaled), so pass the scroller for triggers inside a scroll area.
   */
  collisionBoundary?: TooltipCollisionBoundary
  /**
   * Maximum popup width in pixels. Defaults to 400 (fits about 70
   * characters); longer content wraps.
   */
  maxWidth?: number
  /** Delay before opening in milliseconds */
  delay?: number
  /** Delay before closing in milliseconds */
  closeDelay?: number
  /** Whether clicking the trigger closes the tooltip. Defaults to true. */
  closeOnClick?: boolean
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes; `eventDetails.cancel()` keeps it from changing */
  onOpenChange?: (
    open: boolean,
    eventDetails: BaseTooltip.Root.ChangeEventDetails,
  ) => void
  /** Callback when the open or close animation finishes */
  onOpenChangeComplete?: (open: boolean) => void
  /** When true, the tooltip is disabled and won't show */
  disabled?: boolean
  /** Redacts the popup from session recordings, which a `data-private` on the trigger can't reach. */
  private?: boolean
  /** The trigger element - single ReactElement required by Base UI's render prop */
  children: ReactElement
}
